namespace RS
{
    export interface ExtensionProperty<TClass, TValue>
    {
        get?: (this: TClass) => TValue;
        set?: (this: TClass) => TValue;
    }

    export type ExtensionMethod<TClass> = (this: TClass, ...args: any[]) => any;

    type ExtensionMap<TClass, TExtensions> = { [P in keyof TExtensions]: ExtensionMethod<TClass> | ExtensionProperty<TClass, any> };

    export namespace Strategy
    {
        export type Class<T extends object> = { new(): T; };

        /**
         * Encapsulates an object which provides instances of a strategy.
         */
        export interface IProvider<T extends object, TExtensions extends (ExtensionMap<T, TExtensions> | {}) = {}>
        {
            /** Gets an instance of this strategy to use. */
            get(): T & TExtensions;

            /** Registers the specified class as a concrete class of this factory. */
            register(cl: Class<T>): void;
        }

        function applyExtensions<T extends object, TExtensions extends ExtensionMap<T, TExtensions>>(target: Class<T>, extensions: TExtensions): Class<T & TExtensions>
        {
            const BaseClass = target as Class<object>;
            class ExtendedClass extends BaseClass {}

            for (const key in extensions)
            {
                const extension: ExtensionMethod<T> | ExtensionProperty<T, any> = extensions[key];
                if (Is.func(extension))
                {
                    (ExtendedClass as Class<object>).prototype[key] = extension;
                }
                else
                {
                    Object.defineProperty(ExtendedClass.prototype, key,
                    {
                        get: extension.get,
                        set: extension.set
                    });
                }
            }

            return ExtendedClass as Class<T & TExtensions>;
        }

        class FactoryProvider<T extends object, TExtensions extends ExtensionMap<T, TExtensions>> implements IProvider<T, TExtensions>
        {
            private _class: Class<T & TExtensions> | null = null;

            public constructor(private _errors: boolean, private _overridable: boolean, private _extensions: TExtensions) { }

            /** Gets an instance of this strategy to use. */
            public get(): T & TExtensions
            {
                if (this._class == null)
                {
                    if (!this._errors) { return null; }
                    throw new Error("Tried to instantiate strategy which is not backed by a concrete implementation");
                }
                return new this._class();
            }

            /** Registers the specified class as a concrete class of this factory. */
            public register(cl: Class<T>): void
            {
                if (this._class != null && !this._overridable) { throw new Error("Tried to register concrete implementation for strategy when one was already registered"); }
                this._class = this._extensions ? applyExtensions(cl, this._extensions) : cl as Class<T & TExtensions>;
            }
        }

        class SingletonProvider<T extends object, TExtensions extends ExtensionMap<T, TExtensions>> implements IProvider<T, TExtensions>
        {
            private _class: Class<T & TExtensions> | null = null;
            private _instance: (T & TExtensions) | null = null;

            public constructor(private _errors: boolean, private _overridable: boolean, private _extensions?: TExtensions) { }

            /** Gets an instance of this strategy to use. */
            public get(): T & TExtensions
            {
                if (this._instance == null)
                {
                    if (this._class == null)
                    {
                        if (!this._errors) { return null; }
                        throw new Error("Tried to instantiate strategy which is not backed by a concrete implementation");
                    }
                    this._instance = new this._class();
                }
                return this._instance;
            }

            /** Registers the specified class as a concrete class of this factory. */
            public register(cl: Class<T>): void
            {
                if (this._class != null && !this._overridable) { throw new Error("Tried to register concrete implementation for strategy when one was already registered"); }
                if (this._instance)
                {
                    if (Is.disposable(this._instance)) { this._instance.dispose(); }
                    this._instance = null;
                }
                this._class = this._extensions ? applyExtensions(cl, this._extensions) : cl as Class<T & TExtensions>;
            }
        }

        /**
         * Declares a strategy of the specified type.
         * @param type
         * @param suppressErrors If true, calls to get() will return null upon failure instead of throwing.
         * @param allowOverrides If true, multiple concrete classes can be registered, and the latest one is the one that will be used when instantiating.
         */
        export function declare<T extends object>(type?: Type, suppressErrors?: boolean, allowOverrides?: boolean): IProvider<T>

        /**
         * Declares a strategy of the specified type.
         * @param type
         * @param suppressErrors If true, calls to get() will return null upon failure instead of throwing.
         * @param allowOverrides If true, multiple concrete classes can be registered, and the latest one is the one that will be used when instantiating.
         * @param extensions An object containing methods and properties to be applied to registered implementations.
         */
        export function declare<T extends object, TExtensions extends ExtensionMap<T, TExtensions>>(type?: Type, suppressErrors?: boolean, allowOverrides?: boolean, extensions?: TExtensions): IProvider<T, TExtensions>

        export function declare<T extends object, TExtensions extends ExtensionMap<T, TExtensions>>(type = Type.Instance, suppressErrors: boolean = false, allowOverrides: boolean = false, extensions?: TExtensions): IProvider<T, TExtensions>
        {
            switch (type)
            {
                case Type.Instance:
                    return new FactoryProvider(!suppressErrors, allowOverrides, extensions);
                case Type.Singleton:
                    return new SingletonProvider(!suppressErrors, allowOverrides, extensions);
                default:
                    return null;
            }
        }

        /**
         * Defines the type of a strategy.
         */
        export enum Type
        {
            /** Strategies are independent instances. */
            Instance,

            /** Strategies are a single shared instance. */
            Singleton
        }
    }
}